import React from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"

import { useAppDispatch, useAppSelector } from "redux/hooks"
import { closeDialog } from "redux/features/dialog/dialogSlice"
import { Progress } from "components/ui/Spinner"
import Close from "components/ui/ModalClose"
import LoadableEntity from "components/ui/LoadableEntity"
import {
  LoaderContainer,
  ErrorContainer,
  Headline,
  Title,
  Description,
} from "hocs/features/modal/elements"
import { selectId } from "redux/features/auth/authSlice"
import {
  selectSubscriptionEligibilityById,
  selectFetchStatus,
} from "redux/features/subscription_eligibility/subscriptionEligibilitySlice"
import { SubscriptionEligibilityType } from "types"

export function withModalSubscriptionEligibility<GenericType>(
  WrappedComponent: React.ComponentType<GenericType>,
) {
  const withModalSubscriptionEligibility = (props: GenericType) => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { t } = useTranslation()

    const clientId = useAppSelector((state) => selectId(state)) || ""
    const subscriptionEligibility = useAppSelector(
      (state) =>
        selectSubscriptionEligibilityById(
          state,
          clientId,
        ) as SubscriptionEligibilityType,
    )
    const { need_subscription, allow_subscribe } = subscriptionEligibility

    const fetchStatus = useAppSelector((state) => selectFetchStatus(state))

    const handleClose = () => {
      dispatch(closeDialog())
      navigate("/")
    }

    const renderLoader = () => {
      return (
        <LoaderContainer>
          <Progress color="primary" />
        </LoaderContainer>
      )
    }

    const renderError = () => {
      return (
        <ErrorContainer>
          <Close onClick={handleClose} />
          <Headline>
            <Title variant="h3">{t("features.client.loading.title")}</Title>
            <Title variant="h5">
              {t("features.client.loading.error.title")}
            </Title>
            <Description variant="h6">
              {t("features.client.loading.error.description")}
            </Description>
          </Headline>
        </ErrorContainer>
      )
    }

    return (
      <LoadableEntity
        entity={subscriptionEligibility}
        fetchStatus={fetchStatus}
        renderLoading={renderLoader}
        renderError={renderError}
      >
        {(subscriptionEligibility: SubscriptionEligibilityType) => (
          <WrappedComponent
            {...props}
            subscriptionEligibility={subscriptionEligibility}
            needSubscription={need_subscription}
            allowSubscribe={allow_subscribe}
            fetchStatus={fetchStatus}
          />
        )}
      </LoadableEntity>
    )
  }

  return withModalSubscriptionEligibility
}
